import type { UserProfile } from 'firebase/auth'
import type { DocumentReference } from 'firebase/firestore'
import { doc } from 'firebase/firestore'
import { publicUserConverter } from '~/firestore/converters/publicUser'
import { simulatorPermissionsConverter } from '~/firestore/converters/simulatorPermissions'
import { userRolesConverter } from '~/firestore/converters/userRolesConverter'
import type { SimulatorCourse, SimulatorPermission } from '~/types/simulator/simulator'
import type { ThrowSummary } from '~/types/throws'
import type { PublicUser, UserAdminDocument, UserDisplayMetadata, UserRole } from '~/types/user'
import {
    getAdminSimulatorCoursesCollection,
    getAdminSimulatorPermissionsCollection,
    getAdminUserRolesCollection,
    getLeaderboardUserMetadataCollection,
    getStorePricingPlansCollection,
    getUserAdminCollection,
    getUserPricingPlansCollection,
    getUsersCollection,
    getUserThrowSummaries,
} from './collections.client'
import { pricingPlanConverter, type PricingPlan } from './converters/pricingPlanConverter'

export function getUserDoc(uid = 'unknown'): DocumentReference<UserProfile> {
    return doc(getUsersCollection(), uid)
}

export function getThrowSummary(uid = 'unknown', id): DocumentReference<ThrowSummary> {
    return doc(getUserThrowSummaries(uid), id)
}

export function getLeaderboardUserMetadata(
    userId = 'unknown',
): DocumentReference<UserDisplayMetadata> {
    return doc(getLeaderboardUserMetadataCollection(), userId)
}

export function getUserRole(roleId: string): DocumentReference<UserRole> {
    return doc(getAdminUserRolesCollection(), roleId).withConverter(userRolesConverter)
}

export function getUserAdminDoc(uid: string): DocumentReference<UserAdminDocument> {
    return doc(getUserAdminCollection(), uid)
}

export function getSimulatorCourseDoc(courseId: string): DocumentReference<SimulatorCourse> {
    return doc(getAdminSimulatorCoursesCollection(), courseId)
}

export function getPublicUserAdminDoc(): DocumentReference<PublicUser> {
    return doc(getUserAdminCollection(), 'public').withConverter(publicUserConverter)
}

export function getSimulatorPermissionsDoc(id: string): DocumentReference<SimulatorPermission> {
    return doc(getAdminSimulatorPermissionsCollection(), id).withConverter(
        simulatorPermissionsConverter,
    )
}

export function getUserPricingPlan(id: string): DocumentReference<PricingPlan> {
    return doc(getUserPricingPlansCollection(), id).withConverter(pricingPlanConverter)
}

export function getStorePricingPlan(id: string): DocumentReference<PricingPlan> {
    return doc(getStorePricingPlansCollection(), id).withConverter(pricingPlanConverter)
}
